// diskman.js — 软盘文件 popup: a small file explorer over the mounted floppy,
// opened by the 管理软盘文件 button in the bottom control bar.
//
// Read flow:   machine.exportFloppy() -> fatdisk.mount() -> render listing.
// Write flow:  refuse while the FDC is busy (warn-and-block) -> export FRESH
//              bytes -> mutate the private copy via fatdisk -> ONLY on success
//              machine.swapFloppy() -> re-render. A failed FAT operation never
//              reaches the emulator, so it cannot corrupt the mounted disk.
import { mount, suggestName, FatError } from './fatdisk.js';
import { ModalDialog } from './help.js';

const fmtSize = (n) => (n >= 10240 ? `${Math.round(n / 1024)}K` : `${n}`);

export class DiskManager {
  constructor(machine, els) {
    this.machine = machine;
    this.els = els; // {trigger, overlay, background, body, path, list, foot,
                    //  note, uploadBtn, uploadFile, mkdirBtn, refreshBtn, busyLed,
                    //  onOpen?}
    this.cwd = [];      // path segments from root
    this.disk = null;   // current mount (read model)
    this.fddBusy = false;
    this._writing = false; // one write op (export->mutate->swap) at a time

    // The popup chrome (backdrop, ×, Esc, focus trap, inert background) is the
    // shared machine-case modal; opening always re-reads the disk.
    this.dialog = new ModalDialog({
      trigger: els.trigger,
      overlay: els.overlay,
      background: els.background,
      onOpen: () => {
        if (els.onOpen) els.onOpen();
        this.refresh();
      },
    });

    els.refreshBtn.addEventListener('click', () => this.refresh());
    els.uploadBtn.addEventListener('click', () => els.uploadFile.click());
    els.uploadFile.addEventListener('change', () => {
      const files = [...els.uploadFile.files];
      els.uploadFile.value = '';
      this.upload(files);
    });
    els.mkdirBtn.addEventListener('click', () => this.mkdirPrompt());
    els.body.addEventListener('dragover', (e) => e.preventDefault());
    els.body.addEventListener('drop', (e) => {
      e.preventDefault();
      this.upload([...e.dataTransfer.files]);
    });
  }

  get isOpen() { return this.dialog.isOpen; }

  path() { return '/' + this.cwd.join('/'); }
  sub(name) { return (this.cwd.length ? this.path() : '') + '/' + name; }

  // Per-frame: mirror FDC activity onto the LED and write buttons.
  onFrame(snap) {
    if (snap.fddBusy === this.fddBusy) return;
    this.fddBusy = snap.fddBusy;
    this.els.busyLed.classList.toggle('is-on', this.fddBusy);
    this._applyWriteLock();
  }

  _applyWriteLock() {
    const locked = this.fddBusy || this._writing || !this.machine.powered || !this.machine.ready;
    for (const b of [this.els.uploadBtn, this.els.mkdirBtn]) b.disabled = locked;
    for (const b of this.els.list.querySelectorAll('.disk-act[data-write]')) b.disabled = locked;
  }

  note(msg) {
    this.els.note.textContent = msg || '';
    this.els.note.hidden = !msg;
  }

  async refresh() {
    if (!this.machine.ready || !this.machine.powered) {
      this.els.list.textContent = '';
      this.els.foot.textContent = '';
      this.note('电源已关闭');
      return;
    }
    try {
      const bytes = this.machine.exportFloppy();
      if (!bytes) throw new Error('无法读取磁盘');
      this.disk = await mount(bytes);
      this.note('');
      this.render();
    } catch (e) {
      this.disk = null;
      this.els.list.textContent = '';
      this.els.foot.textContent = '';
      this.note(e instanceof FatError ? e.message : `读盘失败: ${e.message || e}`);
    }
  }

  render() {
    const { list, path, foot } = this.els;
    // breadcrumb
    path.textContent = '';
    const rootEl = document.createElement('span');
    rootEl.className = 'disk-crumb';
    rootEl.textContent = 'A:\\';
    rootEl.onclick = () => { this.cwd = []; this.render(); };
    path.appendChild(rootEl);
    this.cwd.forEach((seg, i) => {
      const el = document.createElement('span');
      el.className = 'disk-crumb';
      el.textContent = seg + '\\';
      el.onclick = () => { this.cwd = this.cwd.slice(0, i + 1); this.render(); };
      path.appendChild(el);
    });

    list.textContent = '';
    let entries;
    try {
      entries = this.disk.list(this.path());
    } catch (e) {
      // cwd vanished under us (e.g. refresh after guest deleted it): go home.
      this.cwd = [];
      entries = this.disk.list('/');
    }
    if (this.cwd.length) {
      list.appendChild(this._row({ name: '..', isDir: true, size: 0 }, true));
    }
    for (const e of entries) list.appendChild(this._row(e, false));

    const { freeBytes, totalBytes } = this.disk.freeSpace();
    foot.textContent = `${entries.length} 项 · 剩余 ${Math.round(freeBytes / 1024)} / ${Math.round(totalBytes / 1024)} KB`;
    this._applyWriteLock();
  }

  _row(entry, isUp) {
    const row = document.createElement('div');
    row.className = 'disk-row';
    row.setAttribute('role', 'listitem');

    const name = document.createElement('span');
    name.className = 'disk-name' + (entry.isDir ? ' is-dir' : '');
    name.textContent = (entry.isDir ? '📁 ' : '') + entry.name;
    if (entry.mtime) name.title = entry.mtime.toLocaleString();
    if (isUp) {
      name.onclick = () => { this.cwd.pop(); this.render(); };
    } else if (entry.isDir) {
      name.onclick = () => { this.cwd.push(entry.name); this.render(); };
    }
    row.appendChild(name);

    const size = document.createElement('span');
    size.className = 'disk-size';
    size.textContent = entry.isDir ? '' : fmtSize(entry.size);
    row.appendChild(size);

    const acts = document.createElement('span');
    acts.className = 'disk-acts';
    if (!isUp) {
      if (!entry.isDir) acts.appendChild(this._act('取出', () => this.download(entry)));
      acts.appendChild(this._act('改名', () => this.renameInline(row, entry), true));
      acts.appendChild(this._act('删除', () => this.remove(entry), true));
    }
    row.appendChild(acts);
    return row;
  }

  _act(label, fn, isWrite = false) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'disk-act';
    b.textContent = label;
    if (isWrite) b.dataset.write = '1';
    b.onclick = fn;
    return b;
  }

  download(entry) {
    try {
      const data = this.disk.read(this.sub(entry.name));
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      this.note(e.message || String(e));
    }
  }

  // Shared write-op harness: warn-and-block, fresh export, mutate, swap.
  // Serialized: overlapping ops would each export->mutate->swap and the later
  // swap would clobber the earlier change, so refuse while one is in flight.
  async _mutate(fn) {
    if (this.fddBusy) { this.note('磁盘使用中，请稍候再试'); return false; }
    if (!this.machine.ready || !this.machine.powered) { this.note('电源已关闭'); return false; }
    if (this._writing) return false;
    this._writing = true;
    this._applyWriteLock();
    try {
      const disk = await mount(this.machine.exportFloppy());
      await fn(disk);
      // Re-check FDC busy against the LIVE accessor immediately before the swap:
      // a guest disk op may have started during the async export/mutate above,
      // and committing over it could corrupt the in-progress operation.
      if (this.machine.fddBusy()) throw new Error('磁盘使用中，请稍候再试');
      if (!this.machine.swapFloppy(disk.image())) throw new Error('磁盘回写失败');
      this.disk = disk;
      this.note('');
      this.render();
      return true;
    } catch (e) {
      this.note(e.message || String(e));
      return false;
    } finally {
      this._writing = false;
      this._applyWriteLock();
    }
  }

  async upload(files) {
    if (!files.length) return;
    if (!this.disk) { this.note('读盘失败或未就绪'); return; }
    for (const f of files) {
      const { freeBytes } = this.disk.freeSpace();
      if (f.size > freeBytes) { this.note(`磁盘空间不足: ${f.name}`); break; }
      const bytes = new Uint8Array(await f.arrayBuffer());
      let skipped = false;
      const ok = await this._mutate(async (disk) => {
        // Resolve the target name against the FRESH mutation disk (reflects files
        // created earlier in this same batch and any guest-side changes), not the
        // stale this.disk read model — so both the dedup ~N tail and the FatFs
        // retry avoid names that already exist on the disk being written.
        const listNames = () => disk.list(this.path()).map((e) => e.name);
        let name = f.name;
        const existing = listNames();
        if (existing.some((n) => n.toUpperCase() === name.toUpperCase()) ||
            suggestName([], name) !== name) {
          name = suggestName(existing, f.name);
          if (!window.confirm(`「${f.name}」将以 8.3 文件名「${name}」存盘，继续？`)) { skipped = true; return; }
        }
        try {
          disk.write(this.sub(name), bytes);
        } catch (e) {
          // FatFs is the final arbiter on names: retry once with a sanitized one.
          if (e.rc !== undefined && name === f.name) {
            name = suggestName(listNames(), f.name);
            if (!window.confirm(`「${f.name}」将以 8.3 文件名「${name}」存盘，继续？`)) { skipped = true; return; }
            disk.write(this.sub(name), bytes);
          } else throw e;
        }
      });
      if (skipped) continue;
      if (!ok) break;
    }
  }

  async remove(entry) {
    const p = this.sub(entry.name);
    const q = entry.isDir ? `删除目录「${entry.name}」及其全部内容？` : `删除「${entry.name}」？`;
    if (!window.confirm(q)) return;
    await this._mutate(async (disk) => disk.remove(p, { recursive: entry.isDir }));
  }

  renameInline(row, entry) {
    const nameEl = row.querySelector('.disk-name');
    const input = document.createElement('input');
    input.className = 'disk-rename-input mono';
    input.value = entry.name;
    nameEl.replaceWith(input);
    input.focus();
    input.select();
    let finished = false; // replaceWith() below fires a native blur; latch so it can't re-enter
    const done = async (commit) => {
      if (finished) return;
      finished = true;
      const newName = input.value.trim();
      input.onblur = null;
      input.replaceWith(nameEl);
      if (!commit || !newName || newName === entry.name) return;
      await this._mutate(async (disk) => disk.rename(this.sub(entry.name), this.sub(newName)));
    };
    input.onkeydown = (e) => {
      if (e.key === 'Enter') done(true);
      else if (e.key === 'Escape') done(false);
      e.stopPropagation(); // don't feed the emulator's key capture
    };
    input.onblur = () => done(false);
  }

  mkdirPrompt() {
    const name = window.prompt('新目录名 (8.3):');
    if (!name) return;
    this._mutate(async (disk) => disk.mkdir(this.sub(name.trim())));
  }
}
